import React, { useState } from "react";
import "./ProfileEditDialog.css";

function ProfileEditDialog({ user, onClose, onSave }) {
  const [formData, setFormData] = useState({
    name: user.name || "",
    email: user.email || "",
    bio: user.bio || "",
    birthdate: user.birthdate || "",
    phone: user.phone || "",
    location: user.location || "",
    website: user.website || "",
  });

  const [selectedFile, setSelectedFile] = useState(null);
  const [backgroundFile, setBackgroundFile] = useState(null);

  // 🔐 New password fields
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    setSelectedFile(e.target.files[0]);
  };

  const handleBackgroundChange = (e) => {
    setBackgroundFile(e.target.files[0]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // 🔐 Validate passwords
    if (password && password !== confirmPassword) {
      alert("Passwords do not match.");
      return;
    }

    const fullData = new FormData();
    Object.entries(formData).forEach(([key, value]) => {
      fullData.append(key, value);
    });

    if (password) {
      fullData.append("password", password);
    }

    if (selectedFile) fullData.append("thumbnail", selectedFile);
    if (backgroundFile) fullData.append("background", backgroundFile);

    try {
      const storedUser = JSON.parse(localStorage.getItem("currentUser"));
      if (!storedUser?.username) {
        console.error("❌ No user in localStorage. Cannot update.");
        alert("Failed to update profile.");
        return;
      }

      const response = await fetch(
  `${process.env.REACT_APP_API_URL}/api/users/${storedUser.username}`,
  { method: "PUT", credentials: "include", body: fullData }
);


      let updatedUserData;
      try {
        updatedUserData = await response.clone().json();
      } catch (jsonError) {
        const errorText = await response.text();
        console.error("❌ Failed to parse JSON:", errorText);
        alert("Update completed, but response was not properly formatted.");
        return;
      }

      if (!response.ok) {
        console.error("❌ Server returned non-OK status:", response.status);
        alert("Profile update failed. Please try again.");
        return;
      }

      const updatedUser = updatedUserData.user || updatedUserData;

      const newThumbnail = updatedUser.thumbnail?.startsWith("http")
  ? updatedUser.thumbnail.replace(
      "highroad-001-site1.mtempurl.com",
      `${process.env.REACT_APP_API_URL}`
    )
  : updatedUser.thumbnail
  ? `${process.env.REACT_APP_API_URL}${updatedUser.thumbnail}`
  : "/images/default-thumbnail.jpg";

const newBackground = updatedUser.background?.startsWith("http")
  ? updatedUser.background.replace(
      "highroad-001-site1.mtempurl.com",
      `${process.env.REACT_APP_API_URL}`
    )
  : updatedUser.background
  ? `${process.env.REACT_APP_API_URL}${updatedUser.background}`
  : "/images/default-thumbnail.jpg";


      const updatedUserWithMedia = {
        ...updatedUser,
        thumbnail: newThumbnail,
        background: newBackground,
      };

      localStorage.setItem("currentUser", JSON.stringify(updatedUserWithMedia));

      if (typeof onSave === "function") {
        onSave(updatedUserWithMedia);
      }

      alert("✅ Profile updated!");
      onClose();
    } catch (err) {
      console.error("❌ Error updating profile:", err);
      alert("Failed to update profile.");
    }
  };

  // 🔴 NEW: Handle hard delete
  const handleDeleteAccount = async () => {
    if (
      !window.confirm(
        "⚠ Are you sure you want to permanently delete your account? This cannot be undone."
      )
    ) {
      return;
    }

    try {
      const storedUser = JSON.parse(localStorage.getItem("currentUser"));
      if (!storedUser?.id) {
        alert("User not found in local storage.");
        return;
      }

      const response = await fetch(
  `${process.env.REACT_APP_API_URL}/users/${storedUser.id}`,
  { method: "DELETE", credentials: "include" }
);


      const data = await response.json();

      if (data.success) {
        localStorage.removeItem("currentUser");
        alert("Your account has been permanently deleted.");
        window.location.href = "/"; // redirect to homepage/login
      } else {
        alert(data.error || "Failed to delete account.");
      }
    } catch (err) {
      console.error("❌ Error deleting account:", err);
      alert("Something went wrong.");
    }
  };

  return (
    <div className="profile-edit-overlay">
      <div className="profile-edit-modal">
        <h2>Edit Profile</h2>
        <form onSubmit={handleSubmit} encType="multipart/form-data">
          <input
            name="name"
            placeholder="Name"
            value={formData.name}
            onChange={handleChange}
          />
          <input
            name="email"
            placeholder="Email"
            value={formData.email}
            onChange={handleChange}
          />
          <textarea
            name="bio"
            placeholder="Bio"
            value={formData.bio}
            onChange={handleChange}
          ></textarea>
          <input
            name="birthdate"
            type="date"
            value={formData.birthdate}
            onChange={handleChange}
          />
          <input
            name="phone"
            placeholder="Phone"
            value={formData.phone}
            onChange={handleChange}
          />
          <input
            name="location"
            placeholder="Location"
            value={formData.location}
            onChange={handleChange}
          />
          <input
            name="website"
            placeholder="Website"
            value={formData.website}
            onChange={handleChange}
          />

          {/* 🔐 New password fields */}
          <input
            type="password"
            placeholder="New Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
          <input
            type="password"
            placeholder="Confirm New Password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
          />

          {/* 🔹 Profile Picture Upload */}
          <label>
            Upload New Profile Picture:
            <input type="file" accept="image/*" onChange={handleFileChange} />
          </label>

          {/* 🔹 Background Image Upload */}
          <label>
            Upload New Background Image:
            <input type="file" accept="image/*" onChange={handleBackgroundChange} />
          </label>

          <div className="profile-edit-buttons">
            <button type="submit">Save</button>
            <button type="button" onClick={onClose}>
              Cancel
            </button>
          </div>
        </form>

        {/* 🔴 Delete button at bottom */}
        <div className="delete-account-section">
          <button
            type="button"
            className="delete-account-btn"
            onClick={handleDeleteAccount}
          >
            ❌ Delete Account
          </button>
        </div>
      </div>
    </div>
  );
}

export default ProfileEditDialog;
